import { CommonModule, TitleCasePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  Input,
  OnChanges,
  OnInit,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatTabsModule } from '@angular/material/tabs';

import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { toObservable } from '@angular/core/rxjs-interop';
import { finalize, forkJoin, map, Observable, of, Subject, switchMap, takeUntil, tap } from 'rxjs';

import { UserStore } from '@hmax-order/frontend/state';
import { CompanyHeaderService } from '@hmax-order/frontend/company';
import {
  InputSelectComponent,
  InputUploadFileComponent,
  StorageFile,
  StorageService,
} from '@hmax-order/frontend/ui';
import { CapitalizePipe } from '@hmax-order/frontend/utils';
import { DateUtils } from '@hmax-order/shared/date';
import { Language } from '@hmax-order/shared/enums';
import { formatPersonDocument, formatPhone } from '@hmax-order/shared/format';
import { Cfg, CompanyHeader, Option } from '@hmax-order/shared/types';
import { CfgService } from '../service/cfg.service';

@Component({
  selector: 'hmax-cfg-account',
  standalone: true,
  templateUrl: './account.component.html',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CapitalizePipe,
    CommonModule,
    InputSelectComponent,
    InputUploadFileComponent,
    MatButtonModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatSnackBarModule,
    MatTabsModule,
    ReactiveFormsModule,
    TranslocoModule,
  ],
  providers: [TitleCasePipe],
})
export class CfgAccountComponent implements OnInit, OnChanges {
  private _unsubscribeAll: Subject<any> = new Subject<any>();
  private _cdr = inject(ChangeDetectorRef);
  private _cfgService = inject(CfgService);
  private _companyHeaderService = inject(CompanyHeaderService);
  private _formBuilder = inject(FormBuilder);
  private _snackBar = inject(MatSnackBar);
  private _userStore = inject(UserStore);
  private _titleCasePipe = inject(TitleCasePipe);
  private _translocoService = inject(TranslocoService);
  private _storageService = inject(StorageService);

  @ViewChild(InputUploadFileComponent) private _uploadComponent!: InputUploadFileComponent;

  @Input() cfgs: { [key: string]: Cfg } = {};

  cfg$: Observable<{ [key: string]: Cfg }> = toObservable(this._cfgService.byName);
  userRoles$: Observable<string[] | undefined> = toObservable(this._userStore.getRoles);
  companyHeader$: Observable<CompanyHeader> = toObservable(this._companyHeaderService.companyHeader);

  isOwner!: boolean;
  isDev!: boolean;
  isSuper!: boolean;
  form!: FormGroup;
  languages = ['pt', 'en', 'es'];
  selectedLanguage = this.languages[0];
  options = [];
  optionsCurrency$!: Observable<Option[]>;
  optionsCurrencyOthers$!: Observable<Option[]>;
  optionsCountry$!: Observable<Option[]>;

  optionsLanguage!: Option[];
  optionsTimeZones!: Option[];
  optionsDateFormat: Option[] = [
    {
      name: 'mm/dd/yyyy',
      value: 'mm/dd/yyyy',
    },
    {
      name: 'dd/mm/yyyy',
      value: 'dd/mm/yyyy',
    },
    {
      name: 'yyyy/mm/dd',
      value: 'yyyy/mm/dd',
    },
  ];
  enableSubmitButton = true;
  currentCompanyDocument!: string;
  logoImages: StorageFile[] = [];
  isUploadingLogo = false;
  subscriptionInfoId!: boolean;

  // -----------------------------------------------------------------------------------------------------
  // @ Lifecycle hooks
  // -----------------------------------------------------------------------------------------------------

  ngOnInit(): void {
    this.checkCfgs();
    this.checkUserRoles();
    this.createForm();
    this.getCompanyInfo();
    this.loadTimeZonesOptions();
    this.loadLanguageOptions();
  }

  /**
   * As cfgs chegam de forma assíncrona (apiResource) DEPOIS do `createForm`. Sem repopular
   * o formulário aqui, idioma/fuso/logo nasceriam vazios e o save não teria valor para comparar.
   */
  ngOnChanges(): void {
    if (!this.form) {
      return;
    }
    this.form.patchValue({
      logoUrl: this.cfgs?.['logourl']?.value,
      language: this.cfgs?.['language']?.value,
      timezone: this.cfgs?.['timezone']?.value,
    });
  }

  private checkCfgs(): void {
    this.cfg$?.pipe(takeUntil(this._unsubscribeAll)).subscribe({
      next: (cfgs) => {
        this.cfgs = cfgs;
        const logoUrl = cfgs?.['logourl']?.value;
        this.logoImages = logoUrl ? [{ url: logoUrl }] : [];
      },
    });
  }

  private checkUserRoles(): void {
    this.userRoles$?.pipe(takeUntil(this._unsubscribeAll)).subscribe({
      next: (roles: string[] | undefined) => {
        this.isSuper = roles?.includes('SUPER') ?? false;
        this.isOwner = roles?.includes('OWNER') ?? false;
        this.isDev = roles?.includes('DEV') ?? false;
      },
    });
  }

  /**
   * Buscar dados da empresa
   */
  private getCompanyInfo(): void {
    this.companyHeader$.pipe(takeUntil(this._unsubscribeAll)).subscribe({
      next: (company) => {
        const { document, phone, name, email } = company as CompanyHeader;
        const typeDocument = document && document?.length > 11 ? 'cnpj' : 'cpf';
        const documentFormat = document ? formatPersonDocument(document, typeDocument) : '';
        const phoneFormat = phone ? formatPhone(phone) : '';

        this.currentCompanyDocument = document || '';

        this.form.patchValue({
          company: name ?? '',
          document: documentFormat,
          email: email ?? '',
          phone: phoneFormat,
        });

        this._cdr.detectChanges();
      },
    });
  }

  private createForm(): void {
    const cfgs = this.cfgs;

    // Os controles abaixo precisam existir em CfgName e em defaultCfgs (backend).
    // Controle sem cfg correspondente nunca é salvo — ver cfg-contract.spec.ts.
    this.form = this._formBuilder.group({
      company: [{ value: '', disabled: true }],
      document: [{ value: '', disabled: true }],
      email: [{ value: '', disabled: true }, Validators.email],
      phone: [{ value: '', disabled: true }],
      logoUrl: [{ value: cfgs?.['logourl']?.value, disabled: cfgs?.['logourl']?.superUser && !this.isSuper }],
      language: [{ value: cfgs?.['language']?.value, disabled: cfgs?.['language']?.superUser && !this.isSuper }],
      timezone: [{ value: cfgs?.['timezone']?.value, disabled: cfgs?.['timezone']?.superUser && !this.isSuper }],
    });
  }

  private loadTimeZonesOptions(): void {
    const timeZones: string[] = DateUtils.getTimezones();
    this.optionsTimeZones = timeZones.map((timeZone) => {
      return {
        name: timeZone,
        value: timeZone,
      } as Option;
    });
  }

  /**
   * Carregar opções de idioma
   */
  private loadLanguageOptions(): void {
    this.optionsLanguage = Object.entries(Language).map(([key, value]) => {
      return {
        name: key,
        value: value,
      } as Option;
    });
  }

  /**
   * Transformar opções do formulário em cfgs para envio ao back-end
   */
  private formToCfgs(): Cfg[] {
    return this._cfgService.formToCfgs(this.form.value, this.cfgs, this.isSuper);
  }

  /**
   * Verificar se a configuração deve ser exibida para o usuário
   * @param option Nome da configuração
   */
  shouldShowOptions(option: string): boolean {
    return this._cfgService.shouldShowOptions(option, this.cfgs, this.isSuper);
  }

  /**
   * Validar formulário e enviar alteração para o back-end
   */
  save(): void {
    if (this.form.invalid) {
      this.form.markAsTouched();
      this.form.markAsDirty();
      return;
    }

    this.enableSubmitButton = false;

    this.handleLogoUpload()
      .pipe(
        switchMap(() => {
          const cfgs: Cfg[] = this.formToCfgs();
          if (!cfgs.length) return of(null);
          return this._cfgService.update(cfgs);
        }),
        takeUntil(this._unsubscribeAll),
        finalize(() => {
          this.enableSubmitButton = true;
          this._cdr.detectChanges();
        }),
      )
      .subscribe({
        next: () => {
          const message = this._translocoService.translate('update-success');
          this._snackBar.open(message, 'X', { duration: 3000 });
        },
      });
  }

  private handleLogoUpload(): Observable<void> {
    const pendingDeletions = this._uploadComponent?.getPendingDeletions() ?? [];
    const hasPendingFiles = this._uploadComponent?.hasPendingFiles() ?? false;

    if (!hasPendingFiles && pendingDeletions.length === 0) {
      return of(undefined);
    }

    this.isUploadingLogo = true;
    this._cdr.detectChanges();

    return this.deleteLogos(pendingDeletions).pipe(
      switchMap(() => this.uploadLogo(hasPendingFiles)),
      finalize(() => {
        this.isUploadingLogo = false;
        this._cdr.detectChanges();
      }),
    );
  }

  private deleteLogos(pendingDeletions: StorageFile[]): Observable<void> {
    if (pendingDeletions.length === 0) {
      return of(undefined);
    }

    const deleteLogo = pendingDeletions.filter((img) => !img.url?.startsWith('https://storage.googleapis.com/hmax-public-images/'));

    if (deleteLogo.length === 0) {
      this._uploadComponent.clearAfterDeletion(pendingDeletions.map((d) => d.url));
      return of(undefined);
    }

    return forkJoin(deleteLogo.map((img) => this._storageService.deleteFile(img.url))).pipe(
      tap(() => this._uploadComponent.clearAfterDeletion(pendingDeletions.map((d) => d.url))),
      map(() => undefined),
    );
  }

  private uploadLogo(hasPendingFiles: boolean): Observable<void> {
    if (!hasPendingFiles) {
      this.form.get('logoUrl')?.setValue('');
      this.logoImages = [];
      this._companyHeaderService.updateLocal({ logo: '' });
      return of(undefined);
    }

    const file = this._uploadComponent.getPendingFiles()[0];
    return this._storageService.upload(file, 'logo', file.name).pipe(
      tap((uploaded) => {
        this._uploadComponent.clearAfterUpload();
        this.form.get('logoUrl')?.setValue(uploaded.url);
        this.logoImages = [uploaded];
        this._companyHeaderService.updateLocal({ logo: uploaded.url });
      }),
      map(() => undefined),
    );
  }

  /**
   * Alterar idioma selecionado
   * @param language Idioma selecionado
   */
  selectLanguage(language: string): void {
    if (language !== null) {
      this.selectedLanguage = language;
    }
  }

  /**
   * Retornar o idioma com a primeira letra em maiúsculo
   * @param text Idioma a ser transformado
   */
  getLanguageToCamelCase(text: string): string {
    return this._titleCasePipe.transform(text);
  }

  cancel(): void {
    this.createForm();
    const companyHeader = this._companyHeaderService.companyHeader();
    if (companyHeader) {
      const { document, phone, name, email } = companyHeader;
      const typeDocument = document && document?.length > 11 ? 'cnpj' : 'cpf';
      const documentFormat = document ? formatPersonDocument(document, typeDocument) : '';
      const phoneFormat = phone ? formatPhone(phone) : '';
      this.form.patchValue({
        company: name ?? '',
        document: documentFormat,
        email: email ?? '',
        phone: phoneFormat,
      });
    }
    this.logoImages = this.cfgs?.['logourl']?.value ? [{ url: this.cfgs['logourl'].value }] : [];
    this._uploadComponent?.reset();
    this._cdr.detectChanges();
  }
}
